import { LRUMap } from "lru_map";

import { captureMessage } from "@/error/sentry";
import { SECONDS } from "@/model/Settings";

import { LocalStore, versioned } from "../LocalStore";
import type { Line } from "./lineBreaker";

interface StoreEntry {
  key: string;
  value: FontData;
}

type FontData = EditFontData | DisplayFontData;

// font data for text with unprocessed links (used for edit mode)
export interface EditFontData {
  size: number;
  lines: Line[];
}

// font data for text with processed links (used for non-edit mode)
export interface DisplayFontData extends EditFontData {
  display?: EditFontData; // if the text contains links, the line breaks are different for display mode
  htmlLines: string[];
  textLines: string[];
}

function isDisplayFontData(data: FontData): data is DisplayFontData {
  return "htmlLines" in data;
}

const store = new LocalStore<StoreEntry[]>("fontCache", {
  sedes: versioned(4),
  saveDelay: 5 * SECONDS,
});
const capacity = 10000;
const cache = loadCache();

export function cacheEditFontData(key: string, calcEditFontData: () => EditFontData): EditFontData {
  // use the cache to have the same line breaks as display mode, even if there can be slight rounding differences
  return cache.get(key) || updateCache(key, calcEditFontData());
}

export function cacheDisplayFontData(
  key: string,
  calcEditFontData: () => EditFontData,
  calcDisplayFontData: (data: EditFontData) => DisplayFontData,
): DisplayFontData {
  const cached = cache.get(key);
  return applyDisplay(cached ? fromCache(cached) : calc());

  function fromCache(data: FontData) {
    return !isDisplayFontData(data) ? updateCache(key, calcDisplayFontData(data)) : data;
  }

  function calc() {
    return updateCache(key, calcDisplayFontData(calcEditFontData()));
  }

  function applyDisplay(fontData: DisplayFontData) {
    return "display" in fontData ? { ...fontData, ...fontData.display } : fontData;
  }
}

function updateCache<T extends FontData>(key: string, fontData: T): T {
  if (!fontData.size) {
    captureMessage("Could not calc fontData");
    fontData.size = 100;
    return fontData;
  }

  cache.set(key, fontData);
  saveCache();

  return fontData;
}

function loadCache(): LRUMap<string, FontData> {
  const entries: Array<[string, FontData]> = (store.load() || []).map((it) => [it.key, it.value]);
  return new LRUMap(capacity, entries);
}

function saveCache() {
  store.save(cache.toJSON());
}

export function clearCache() {
  cache.clear();
  saveCache();
}
